import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { fg } from '@atlassian/jira-feature-gating';
import SelectAsanaBoard from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-asana-board';
import SelectBusinessProject from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-business-project';
import SelectClickupList from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-clickup-list';
import SelectMondayBoard from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-monday-board';
import SelectSoftwareProject from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-software-project';
import SelectTrelloBoard from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/select-trello-board';
import AsanaIllustrationSpork from '../../../common/assets/AsanaSelectBoardIllustrationSpork.svg';
import AsanaSelectBoardJiraIllustration from '../../../common/assets/AsanaSelectBoardJiraIllustration.svg';
import ClickupIllustrationSpork from '../../../common/assets/ClickupSelectListIllustrationSpork.svg';
import ClickupIllustrationStandard from '../../../common/assets/ClickupSelectListIllustrationStandard.svg';
import JiraBusinessSelectProjectIllustration from '../../../common/assets/JiraBusinessSelectProjectIllustration.svg';
import JiraIllustrationSpork from '../../../common/assets/JiraSelectBoardIllustrationSpork.svg';
import MondayIllustrationSpork from '../../../common/assets/MondaySelectBoardIllustrationSpork.svg';
import MondayIllustrationStandard from '../../../common/assets/MondaySelectBoardIllustrationStandard.svg';
import TrelloIllustrationSpork from '../../../common/assets/TrelloSelectBoardIllustrationSpork.svg';
import TrelloIllustrationStandard from '../../../common/assets/TrelloSelectBoardIllustrationStandard.svg';
import TrelloIllustrationJsw from '../../../common/assets/TrelloSelectBoardJswIllustartion.svg';
import {
	ASANA,
	MONDAY,
	type SupportedThirdParty,
	TRELLO,
	JIRA_SOFTWARE,
	CLICK_UP,
	JIRA_BUSINESS,
} from '../../../common/constants/import-type';
import { ProjectTypes } from '../../../common/constants/project';

type Props = {
	thirdParty: SupportedThirdParty;
	projectType: ProjectType | null;
};

export const ThirdPartyIllustration = ({ thirdParty, projectType }: Props) => {
	const illustration = useMemo(() => {
		if (projectType === ProjectTypes.BUSINESS) {
			switch (thirdParty) {
				case MONDAY:
					return (
						<Illustration
							src={MondayIllustrationSpork}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.monday"
						/>
					);
				case ASANA:
					return (
						<Illustration
							src={AsanaIllustrationSpork}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.asana"
						/>
					);
				case TRELLO:
					return (
						<Illustration
							src={TrelloIllustrationSpork}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.trello"
						/>
					);
				case JIRA_SOFTWARE:
					return (
						<Illustration
							src={JiraIllustrationSpork}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira"
						/>
					);
				case CLICK_UP:
					return (
						<Illustration
							src={ClickupIllustrationSpork}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.clickup"
						/>
					);
				default:
					return null;
			}
		} else if (projectType === ProjectTypes.SOFTWARE) {
			switch (thirdParty) {
				case TRELLO:
					return (
						<Illustration
							src={TrelloIllustrationJsw}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.trello-jsw"
						/>
					);
				case ASANA:
					return (
						<Illustration
							src={AsanaSelectBoardJiraIllustration}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.asana-jsw"
						/>
					);
				case JIRA_BUSINESS:
					return (
						<Illustration
							src={JiraBusinessSelectProjectIllustration}
							alt=""
							data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira-business"
						/>
					);
				default:
					return null;
			}
		} else {
			return null;
		}
	}, [projectType, thirdParty]);

	const illustrationNew = useMemo(() => {
		switch (thirdParty) {
			case MONDAY:
				return (
					<Illustration
						src={MondayIllustrationStandard}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.monday"
					/>
				);
			case ASANA:
				return (
					<Illustration
						src={AsanaSelectBoardJiraIllustration}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.asana"
					/>
				);
			case TRELLO:
				return (
					<Illustration
						src={TrelloIllustrationStandard}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.trello"
					/>
				);
			case JIRA_SOFTWARE:
				return (
					<Illustration
						src={JiraIllustrationSpork}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira"
					/>
				);
			case CLICK_UP:
				return (
					<Illustration
						src={ClickupIllustrationStandard}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.clickup"
					/>
				);
			case JIRA_BUSINESS:
				return (
					<Illustration
						src={JiraBusinessSelectProjectIllustration}
						alt=""
						data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira-business"
					/>
				);
			default:
				return null;
		}
	}, [thirdParty]);

	if (fg('x2c-consolidate-assets')) {
		return (
			<Box xcss={containerStyles}>{ThirdPartyIllustrationNew({ thirdParty, projectType })}</Box>
		);
	}

	if (fg('enable-x2c-standardised-flow')) {
		return illustrationNew;
	}
	return illustration;
};

export const ThirdPartyIllustrationNew = ({ thirdParty }: Props) => {
	switch (thirdParty) {
		case MONDAY:
			return (
				<SelectMondayBoard
					alt="select monday board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.monday"
				/>
			);
		case ASANA:
			return (
				<SelectAsanaBoard
					alt="select asana board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.asana"
				/>
			);
		case TRELLO:
			return (
				<SelectTrelloBoard
					alt="select trello board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.trello"
				/>
			);
		case JIRA_SOFTWARE:
			return (
				<SelectSoftwareProject
					alt="select jira software board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira-software"
				/>
			);
		case CLICK_UP:
			return (
				<SelectClickupList
					alt="select clickup board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.clickup"
				/>
			);
		case JIRA_BUSINESS:
			return (
				<SelectBusinessProject
					alt="select jira business board"
					data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.third-party-illustration.jira-business"
				/>
			);
		default:
			return null;
	}
};

const containerStyles = xcss({
	width: '400px',
	marginTop: 'space.1000',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Illustration = styled.img({
	width: '400px',
	marginTop: token('space.1000', '80px'),
});
